import { Component, OnDestroy, OnInit } from '@angular/core';
import { ICampaign } from '../../../store/models/campaigns.model';
import {
  DataSourceParams,
  EFilterSize,
  EFilterType,
  ETableColumnType,
  ITableColumn,
  ITableFIlterGroup,
} from '../../../store/models/table.model';
import { IContactItem } from '../../../store/models/contact.model';
import { SessionService } from '../../../store/services/session.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserType } from '../../../store/models/user.model';
import { ApiDataSource } from '../../../datasources/api.datasrouce';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { ButtonSelectComponent } from '../../../shared/ui/button-select/button-select.component';
import { ButtonColor, ButtonSize, IButtonSelectItem } from '../../../store/models/button.model';
import { EInfoSelectItemType } from '../../../store/models/select.model';
import { openDialog, openSnackbar } from '../../../store/actions/page.action';
import { updateLeftMenuCount } from '../../../store/actions/meeting.action';
import { ContactsService } from '../../../store/services/contacts.service';
import { Store } from '@ngrx/store';
import { IReducers } from '../../../store/reducers';
import { RContentComponent } from '../../../dialogs/quick-response/r-content.component';
import { DialogData } from '../../../store/models/dialog.model';
import { InfoTipsComponent } from '../../../dialogs/info-tips/info-tips.component';

@Component({
  selector: 'app-next-service-date-dialog',
  templateUrl: './next-service-date-dialog.component.html',
  styleUrls: ['./next-service-date-dialog.component.scss'],
})
export class NextServiceDateDialogComponent implements OnInit, OnDestroy {

  item: ICampaign;

  tableColumns: ITableColumn<IContactItem>[] = [
    {
      id: 'contact_firstname',
      title: 'Contact',
      renderer: (contact) => {
        return `${contact.contact_firstname || ''} ${contact.contact_lastname || ''}`;
      },
      sortable: true,
    },
    {
      id: 'contact_email',
      title: 'Email Address',
      sortable: true,
    },
    {
      id: 'contact_company',
      title: 'Company',
      sortable: true,
    },
    {
      id: 'contact_valid',
      title: 'Validate',
    },
    {
      id: 'contact_status',
      title: 'Contact Status',
      sortable: true,
    },
    {
      id: 'email_position',
      title: 'Email Position',
      sortable: true,
    },
    {
      id: 'next_email',
      title: 'Next Email',
      isDateColumn: true,
      sortable: true,
    },
    {
      id: 'group_name',
      title: 'Group',
      sortable: true,
    },
  ];

  tableFilters: ITableFIlterGroup[] = [
    {
      filters: [
        {
          type: EFilterType.SEARCH,
          size: EFilterSize.HALF,
          items: [{
            label: 'Search',
            id: 'search',
          },
          ],
        },
      ],
    },
  ];

  dataSource: ApiDataSource<IContactItem>;

  dataSourceParams: DataSourceParams;

  componentReady: boolean;

  private readonly destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly sessionService: SessionService,
    private readonly http: HttpClient,
    private readonly contactsService: ContactsService,
    private readonly store: Store<IReducers>,
  ) {
  }

  ngOnInit(): void {
    this.sessionService.onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        if (session) {
          if ([UserType.MASTER, UserType.PARTNER].indexOf(session.user_type) !== -1) {
            this.tableColumns.push({
              id: 'account_firstname',
              title: 'Representative',
              renderer: contact => `${contact.account_firstname || ''} ${contact.account_lastname || ''}`,
            });
          } else if (session.user_type === UserType.SINGLE_USER && (session.permissions &&
            session.permissions.can_find_template)) {
            this.tableColumns.push({
              id: 'response',
              title: 'Reply',
              type: ETableColumnType.COMPONENT,
              componentProperties: {
                component: ButtonSelectComponent,
                mapData: {
                  buttonProperties: {
                    instance: 'static',
                    target: {
                      text: 'Reply',
                      size: ButtonSize.SMALL,
                      color: ButtonColor.ORANGE,
                    },
                  },
                  selection: {
                    instance: 'function',
                    target: rowItem => {
                      return selectItem => this.openItemDialog(rowItem, selectItem);
                    },
                  },
                  items: {
                    instance: 'function',
                    target: (item: IContactItem) => {
                      const targetItems: IButtonSelectItem[] = [
                        {
                          label: 'Opportunities',
                          type: EInfoSelectItemType.TYPE_LINK,
                          tag: 'main',
                          dialogContent: this.getDialogData('opportunities'),
                          children: [
                            {
                              label: 'Meetings',
                              tag: 'meeting',
                              type: EInfoSelectItemType.TYPE_LINK,
                              dialogContent: this.getDialogData('meeting'),
                            },
                            {
                              label: 'Order/Quote',
                              tag: 'order',
                              type: EInfoSelectItemType.TYPE_LINK,
                              dialogContent: this.getDialogData('order'),
                            },
                          ],
                        },
                        {
                          label: 'Cycle',
                          tag: 'cycle',
                          type: EInfoSelectItemType.TYPE_LINK,
                          dialogContent: this.getDialogData('cycle'),
                        },
                        {
                          label: 'Follow up',
                          tag: 'followup',
                          type: EInfoSelectItemType.TYPE_LINK,
                          dialogContent: this.getDialogData('follow_up'),
                        },
                        {
                          label: 'Inactive',
                          tag: 'inactive',
                          type: EInfoSelectItemType.TYPE_LINK,
                          dialogContent: this.getDialogData('inactive'),
                        },
                        {
                          label: 'Remove',
                          tag: 'remove',
                          type: EInfoSelectItemType.TYPE_LINK,
                          dialogContent: this.getDialogData('remove'),
                        },
                        {
                          label: 'Conversation',
                          tag: 'conversation',
                          type: EInfoSelectItemType.TYPE_LINK,
                          dialogContent: this.getDialogData('conversation'),
                        },
                        {
                          label: 'Enter Activity',
                          tag: 'enter_activity',
                          type: EInfoSelectItemType.TYPE_LINK,
                          dialogContent: this.getDialogData('enter_activity'),
                        },
                      ];

                      if (
                        !item.active_for_campaigns
                      ) {
                        targetItems.push({
                          label: 'Back to campaign',
                          type: EInfoSelectItemType.TYPE_NORMAL,
                          dialogContent: this.getDialogData('back_to_campaign'),
                          onClick: () => {
                            this.contactsService
                              .turnBackContactToCampaign(item.contact_id)
                              .subscribe(
                                result => {
                                  if (result && result.success) {
                                    this.store.dispatch(
                                      openSnackbar({
                                        snackbarData: {
                                          data: {
                                            type: 'success',
                                            message: `Contact ${item.contact_firstname} ${item.contact_lastname} moved back to campaign`,
                                          },
                                          duration: 5000,
                                          verticalPosition: 'bottom',
                                        },
                                      }),
                                    );
                                    this.dataSource.refresh();
                                  }
                                },
                                error =>
                                  this.store.dispatch(
                                    openSnackbar({
                                      snackbarData: {
                                        data: {
                                          type: 'error',
                                          message: error.error,
                                        },
                                      },
                                    }),
                                  ),
                              );
                          },
                        });
                      }

                      return targetItems;
                    },
                  },
                },
              },
            });
          }
          this.dataSourceParams = {
            page: 1,
            pageSize: 10,
            sortAttr: 'email_position',
            sortDir: 'asc',
            filters: {
              campaign_id: this.item.campaign_id.toString(),
              contact_status: 'active',
            },
          };

          this.dataSource = new ApiDataSource(this.http, environment.apiUrl + '/v1/contacts');
          this.componentReady = true;
        }
      });
  }

  openDialog(item, data) {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: data.label,
          content: RContentComponent,
          componentData: {
            data,
            contact_id: item.contact_id,
            item,
          },
          dialogMinWidth: '1000px',
          onClose: () => this.dataSource.refresh(),
          canClose: true,
          tips: ['cycle', 'followup', 'meeting', 'inactive', 'remove'].indexOf(data.tag) !== -1 ? 'send-update' : null,
        },
      }),
    );
  }

  openAddToCampaignDialog(item, data) {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: data.label,
          content: RContentComponent,
          componentData: {
            data,
          },
          dialogMinWidth: '1000px',
          canClose: true,
          tips: ['cycle', 'followup', 'meeting', 'inactive', 'remove'].indexOf(data.tag) !== -1 ? 'send-update' : null,
          buttons: [
            {
              text: 'Ok',
              actAsClose: true,
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
            },
            {
              text: 'Close',
              actAsClose: true,
              color: ButtonColor.RED,
              size: ButtonSize.NORMAL,
              onClick: () => console.log('cska'),
            },
          ],
          onClose: () => this.dataSource.refresh(),
        },
      }),
    );
  }

  openItemDialog(itemRow, selectedItem) {
    switch (selectedItem.tag) {
      case 'main':
        break;
      case 'got-it':
        this.contactsService
          .quickResponse({ contact_id: itemRow.contact_id }, 'okgotit')
          .subscribe(response => {
            this.store.dispatch(updateLeftMenuCount({ data: {} }));
          });
        break;
      // case 'send-mail':
      //   this.openSendEmailDialog(itemRow, selectedItem);
      //   break;
      case 'send-to-campaign':
        this.openAddToCampaignDialog(itemRow, selectedItem);
        break;
      default:
        this.openDialog(itemRow, selectedItem);
        break;
    }
  }

  getDialogData(type: string): DialogData {
    return {
      content: InfoTipsComponent,
      title: 'Tips',
      canClose: true,
      dialogMinWidth: '700px',
      componentData: {
        type,
      },
    };
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

}
